"use client";

import * as Dialog from "@radix-ui/react-dialog";
import * as NavMenu from "@radix-ui/react-navigation-menu";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@storyblok/react/rsc";
import { default as Link } from "next/link";

import type { ResolvedHeaderStoryblok } from "@/components/molecules/Navbar/Navbar";
import getLink from "@/lib/storyblok/utils/getLink";
import type {
  LinkStoryblok,
  MenuSectionStoryblok,
  MenuStoryblok,
} from "@/types/storyblok-blok-types";

import LocationSelector from "@/components/atoms/LocationSelector";
import { Account } from "@/components/icons/Account";
import { Back } from "@/components/icons/Back";
import ChevronLeft from "@/components/icons/ChevronLeft";
import { CloseIcon } from "@/components/icons/Close";
import { Country } from "@/components/icons/Country";
import useLockBodyScroll from "@/lib/hooks/useLockBodyScroll";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import styles from "./header-bloks.module.css";
import { useCuratedTabs } from "./hooks";
import MenuItem from "./MenuItem";
import MenuLink from "./MenuLink";
import { SectionGrid } from "./SectionGrid";
import { SectionTabs } from "./SectionTabs";

type HeaderBlokParams = {
  blok: ResolvedHeaderStoryblok;
};

type MobileMenuProps = ResolvedHeaderStoryblok & {
  mobileLinks: LinkStoryblok[];
};

const MobileMenu = ({ menu: menus, links, mobileLinks }: MobileMenuProps) => {
  const pathname = usePathname();
  const [activeMenu, setActiveMenu] = useState<MenuStoryblok | null>(null);
  const [activeSection, setActiveSection] =
    useState<MenuSectionStoryblok | null>(null);

  useEffect(() => {
    setActiveMenu(null);
    setActiveSection(null);
  }, [pathname]);
  const { activeUid, setActiveUid } = useCuratedTabs(activeMenu);
  const isCurated = activeMenu?.isCurated;

  return (
    <div className={styles.menu__mobile}>
      <Dialog.Root>
        <Dialog.Trigger className={styles.menu__trigger}>
          {"MENU"}
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className={styles.menu__overlay} />
          <Dialog.Content className={styles.menu__modal}>
            <Dialog.Title asChild>Mobile Navigation Menu</Dialog.Title>
            <div
              className={clsx(
                styles.menu__content,
                activeMenu?.isDenim && styles.viewportPihlDenim
              )}
            >
              <nav className={styles.menu__close__bar}>
                <Dialog.Close asChild>
                  <button className={styles.menu__close__icon}>
                    <CloseIcon />
                  </button>
                </Dialog.Close>
              </nav>

              <div className={styles.menu__list}>
                <div className={styles.scrollArea}>
                  {/* PAGE 1: Default view with top links + menu titles */}
                  {!activeMenu && (
                    <>
                      <section className={styles.top__menu__link}>
                        {/* Top navigation links */}
                        {mobileLinks.map((link) => {
                          const href = getLink(link.link);
                          const typedLink = link as LinkStoryblok & {
                            color?: { value: string; plugin: string };
                          };

                          return (
                            <p key={link._uid} className={styles.top__link}>
                              <Dialog.Close asChild>
                                <Link
                                  href={href ?? "#"}
                                  style={
                                    {
                                      "--link-color": typedLink.color?.value,
                                    } as React.CSSProperties
                                  }
                                >
                                  {link.text}
                                </Link>
                              </Dialog.Close>
                            </p>
                          );
                        })}
                      </section>

                      {/* Section links from current menu */}
                      <section>
                        {menus[0].section
                          ?.filter((section) => section.showOnMobile)
                          .map((section) => (
                            <button
                              key={`section-${section._uid}`}
                              className={styles.menu__item}
                              onClick={() => {
                                setActiveMenu(menus[0]);
                                setActiveSection(section);
                              }}
                            >
                              <span>{section.title}</span>
                              <ChevronLeft />
                            </button>
                          ))}
                      </section>

                      {/* Other menus */}
                      <section>
                        {menus
                          .filter((m) => m.title !== menus[0].title)
                          .map((menu) => (
                            <button
                              key={`menu-${menu._uid}`}
                              className={styles.menu__item}
                              onClick={() => {
                                setActiveMenu(menu);
                                setActiveSection(null);
                              }}
                            >
                              <span>{menu.title}</span>
                              <ChevronLeft />
                            </button>
                          ))}
                      </section>

                      {/* Footer */}
                      <div className={styles.menu__footer}>
                        <Dialog.Close asChild>
                          <Link
                            href="/account"
                            className={styles.menu__footerItem}
                          >
                            <Account />
                            <span>Account</span>
                          </Link>
                        </Dialog.Close>
                        <div className={styles.menu__footerItem}>
                          <Country />
                          <LocationSelector />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* PAGE 2: Active submenu view */}
                {activeMenu && (
                  <div className={styles.menu__slide}>
                    <div className={styles.scrollArea}>
                      <nav className={styles.menu__topbar}>
                        <button
                          className={styles.back__control}
                          onClick={() => setActiveMenu(null)}
                        >
                          <Back />
                          <span className={styles.back__button}>Back</span>
                        </button>
                      </nav>

                      <h2 className={styles.menu__title}>
                        {activeSection?.title || activeMenu.title}
                      </h2>

                      {isCurated && (
                        <SectionTabs
                          sections={activeMenu.section ?? []}
                          activeUid={activeUid}
                          setActiveUid={setActiveUid}
                        />
                      )}

                      {(isCurated
                        ? activeMenu.section?.filter(
                            (s) => s._uid === activeUid
                          )
                        : activeSection
                          ? [activeSection]
                          : activeMenu.section?.filter((s) => s?.showOnMobile)
                      )?.map((section) => (
                        <SectionGrid
                          key={section._uid}
                          section={section}
                          closeDialog
                          isDenim={activeMenu.isDenim}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
};

const HeaderBloks = ({ blok, ...rest }: HeaderBlokParams) => {
  const maxMenuItems = 4;
  const menus = blok.menu as MenuStoryblok[];
  const links = blok.links;

  if (links && menus.length + links.length > maxMenuItems) {
    links.splice(maxMenuItems - menus.length);
  }
  const pathname = usePathname();

  const [activeMenuValue, setActiveMenuValue] = useState<string>("");
  const activeMenu = menus.find((menu) => menu._uid === activeMenuValue);
  const isCuratedMenu = activeMenu?.isCurated;
  const { lockBodyScroll, unlockBodyScroll } = useLockBodyScroll(true);
  const mobileLinks = menus.flatMap((menu) => menu.links || []);

  useEffect(() => {
    if (activeMenuValue) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    return () => unlockBodyScroll();
  }, [activeMenuValue, lockBodyScroll, unlockBodyScroll]);

  useEffect(() => {
    setActiveMenuValue("");
  }, [pathname]);

  return (
    <>
      <div className={styles.menu__desktop}>
        <NavMenu.Root
          value={activeMenuValue}
          onValueChange={setActiveMenuValue}
          className={styles.menuRoot}
          {...storyblokEditable(blok as unknown as SbBlokData)}
        >
          <NavMenu.List className={styles.menuList}>
            {menus.map((menu, i) => (
              <MenuItem
                menu={menu}
                key={menu._uid}
                value={menu._uid}
                topLinks={menu?.links}
                index={i}
                allMenus={i === 0 ? menus : undefined}
              />
            ))}
            {links?.map((link) => <MenuLink {...link} key={link._uid} />)}
          </NavMenu.List>
          <NavMenu.Viewport
            className={clsx(
              styles.viewport,
              isCuratedMenu && styles.viewportFullWidth
            )}
          />

          <div className={styles.navOverlay} />
        </NavMenu.Root>
      </div>
      <MobileMenu {...blok} mobileLinks={mobileLinks} />
    </>
  );
};

export default HeaderBloks;
